import {
  MaskableFieldHeader,
  useMaskableFieldExpanded,
} from "@/components/form-items/MaskableFieldHeader";
import type { MaskableFieldProps } from "@/components/form-items/MaskedFormFieldShell";
import { cn } from "@/lib/utils";
import { createContext, useContext, type ReactNode } from "react";

/** When true, nested fields (e.g. AF.Checkbox) must not render their own mask control. */
const MaskableGroupMemberContext = createContext(false);

export function useInsideMaskableGroup() {
  return useContext(MaskableGroupMemberContext);
}

export function MaskableGroupMemberProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <MaskableGroupMemberContext.Provider value>
      {children}
    </MaskableGroupMemberContext.Provider>
  );
}

type CheckboxGroupMaskProps = {
  groupName: string;
  label: ReactNode;
  children: ReactNode;
  className?: string;
} & MaskableFieldProps;

export function CheckboxGroupMask({
  groupName,
  label,
  children,
  nonMaskable,
  maskable,
  className,
}: CheckboxGroupMaskProps) {
  const { enabled: maskEnabled, showControl, minimized } =
    useMaskableFieldExpanded(groupName, { nonMaskable, maskable });

  if (!maskEnabled || nonMaskable) {
    return <div className={className}>{children}</div>;
  }

  // Masked group → hidden entirely (re-enable it from the field visibility menu).
  if (minimized) {
    return null;
  }

  return (
    <div className={cn("w-full flex flex-col gap-2.5", className)}>
      <MaskableFieldHeader
        fieldName={groupName}
        maskable={maskable}
        className="mb-0 w-full"
        label={label}
      />
      <MaskableGroupMemberContext.Provider value>
        <div className={cn("pt-1 flex flex-col gap-3", !showControl && "hidden")}>
          {children}
        </div>
      </MaskableGroupMemberContext.Provider>
    </div>
  );
}
